import React, { useState, useEffect, useCallback } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Checkbox,
  useToast,
  Spinner,
  Flex,
  Text,
  IconButton,
  VStack,
  HStack,
  Box,
  Badge,
  Divider,
} from "@chakra-ui/react";
import {
  SearchIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CloseIcon,
} from "@chakra-ui/icons";
import { supabase } from "../../lib/supabaseClient";

const PAGE_SIZE = 10;

const TrackSelectionModal = ({ isOpen, onClose, videoId, onTrackAdded }) => {
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTracks, setSelectedTracks] = useState({});
  const toast = useToast();

  // 모달이 열리거나 닫힐 때 상태 초기화
  useEffect(() => {
    if (isOpen) {
      setTracks([]);
      setLoading(false);
      setImporting(false);
      setPage(1);
      setTotalPages(1);
      setSearchTerm("");
      setSearchQuery("");
      setSelectedTracks({});
    }
  }, [isOpen]);

  const fetchTracks = useCallback(async () => {
    setLoading(true);
    let query = supabase.from("track").select("*", { count: "exact" });

    if (searchQuery) {
      const searchFields = [
        "track_name_kr",
        "isrc",
        "track_code",
        "album",
        "musician",
      ];
      const orConditions = searchFields
        .map((field) => `${field}.ilike.%${searchQuery}%`)
        .join(",");
      query = query.or(orConditions);
    }

    const {
      data: result,
      error,
      count,
    } = await query
      .order("created_date", { ascending: false })
      .range((page - 1) * PAGE_SIZE, page * PAGE_SIZE - 1);

    if (error) {
      toast({
        title: "트랙 조회 실패",
        description: error.message,
        status: "error",
      });
      setTracks([]);
    } else {
      setTracks(result);
      setTotalPages(Math.ceil((count ?? 0) / PAGE_SIZE));
    }
    setLoading(false);
  }, [page, searchQuery, toast]);

  useEffect(() => {
    if (isOpen) {
      fetchTracks();
    }
  }, [isOpen, fetchTracks]);

  const handleSelect = (track) => {
    setSelectedTracks((prev) => {
      const newSelected = { ...prev };
      if (newSelected[track.id]) {
        delete newSelected[track.id];
      } else {
        newSelected[track.id] = track;
      }
      return newSelected;
    });
  };

  const handleRemoveSelected = (trackId) => {
    setSelectedTracks((prev) => {
      const newSelected = { ...prev };
      delete newSelected[trackId];
      return newSelected;
    });
  };

  const handleAddTracks = async () => {
    if (Object.keys(selectedTracks).length === 0) {
      toast({ title: "트랙을 선택해주세요.", status: "warning" });
      return;
    }

    setImporting(true);
    const tracksToAdd = Object.values(selectedTracks).map((track) => ({
      video_id: videoId,
      track_id: track.id,
    }));

    const { error } = await supabase.from("video_to_track").insert(tracksToAdd);

    if (error) {
      toast({
        title: "트랙 추가 실패",
        description: error.message,
        status: "error",
      });
    } else {
      toast({
        title: `${tracksToAdd.length}개 트랙이 추가되었습니다.`,
        status: "success",
      });
      onTrackAdded();
      onClose();
      setSelectedTracks({});
    }
    setImporting(false);
  };

  const handleSearchTrigger = () => {
    setPage(1);
    setSearchQuery(searchTerm);
  };

  const renderSelectedTrack = (track) => {
    return (
      <Box>
        <Text fontWeight="medium">{track.track_name_kr || "트랙명 없음"}</Text>
        <Text fontSize="sm" color="gray.600">
          {track.album || "앨범명 없음"} - {track.musician || "뮤지션 없음"}
        </Text>
        <Text fontSize="xs" color="gray.500">
          ISRC: {track.isrc || "없음"} | 코드: {track.track_code || "없음"}
        </Text>
      </Box>
    );
  };

  const displayColumns = [
    { key: "track_name_kr", label: "트랙명" },
    { key: "isrc", label: "ISRC" },
    { key: "track_code", label: "트랙코드" },
    { key: "album", label: "앨범명" },
    { key: "musician", label: "뮤지션" },
  ];

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>트랙 추가</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            {/* 선택된 트랙 섹션 */}
            {Object.keys(selectedTracks).length > 0 && (
              <Box>
                <Text fontWeight="bold" mb={2}>
                  선택된 트랙 ({Object.keys(selectedTracks).length}개)
                </Text>
                <Box
                  border="1px"
                  borderColor="gray.200"
                  borderRadius="md"
                  p={3}
                  maxH="200px"
                  overflowY="auto"
                >
                  <VStack spacing={2} align="stretch">
                    {Object.values(selectedTracks).map((track) => (
                      <Flex
                        key={track.id}
                        justify="space-between"
                        align="center"
                        p={2}
                        bg="gray.50"
                        borderRadius="md"
                      >
                        {renderSelectedTrack(track)}
                        <IconButton
                          size="sm"
                          icon={<CloseIcon />}
                          onClick={() => handleRemoveSelected(track.id)}
                          colorScheme="red"
                          variant="ghost"
                        />
                      </Flex>
                    ))}
                  </VStack>
                </Box>
                <Divider my={4} />
              </Box>
            )}

            <HStack>
              <InputGroup maxW="300px">
                <InputLeftElement
                  pointerEvents="none"
                  children={<SearchIcon color="gray.300" />}
                />
                <Input
                  placeholder="트랙 검색"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearchTrigger()}
                />
              </InputGroup>
              <Button ml={2} onClick={handleSearchTrigger}>
                검색
              </Button>
            </HStack>
            {loading ? (
              <Flex justify="center" p={10}>
                <Spinner />
              </Flex>
            ) : (
              <>
                <Table variant="simple" size="sm">
                  <Thead>
                    <Tr>
                      <Th>
                        <Checkbox
                          isChecked={
                            tracks.length > 0 &&
                            Object.keys(selectedTracks).length === tracks.length
                          }
                          onChange={() => {
                            if (
                              Object.keys(selectedTracks).length ===
                              tracks.length
                            ) {
                              setSelectedTracks({});
                            } else {
                              const newSelected = {};
                              tracks.forEach(
                                (track) => (newSelected[track.id] = track)
                              );
                              setSelectedTracks(newSelected);
                            }
                          }}
                        />
                      </Th>
                      {displayColumns.map((col) => (
                        <Th key={col.key}>{col.label}</Th>
                      ))}
                    </Tr>
                  </Thead>
                  <Tbody>
                    {tracks.map((track) => (
                      <Tr key={track.id}>
                        <Td>
                          <Checkbox
                            isChecked={!!selectedTracks[track.id]}
                            onChange={() => handleSelect(track)}
                          />
                        </Td>
                        {displayColumns.map((col) => (
                          <Td key={col.key}>{track[col.key] || "-"}</Td>
                        ))}
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
                <Flex justify="center" align="center" mt={6}>
                  <IconButton
                    icon={<ChevronLeftIcon />}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    isDisabled={page === 1}
                    size="sm"
                  />
                  <Text mx={4} fontSize="sm">
                    Page {page} of {totalPages}
                  </Text>
                  <IconButton
                    icon={<ChevronRightIcon />}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    isDisabled={page >= totalPages}
                    size="sm"
                  />
                </Flex>
              </>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button
            colorScheme="blue"
            mr={3}
            onClick={handleAddTracks}
            isLoading={importing}
            disabled={Object.keys(selectedTracks).length === 0}
          >
            추가하기 ({Object.keys(selectedTracks).length}개)
          </Button>
          <Button variant="ghost" onClick={onClose}>
            취소
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default TrackSelectionModal;
